/**
 * Type Guard Utilities
 * Type-safe type checking and conversion functions
 * Follows TypeScript 5+ best practices for type narrowing
 */

/**
 * Check if value is a number or numeric string
 * 
 * @param value - Value to check
 * @returns True if value is a number or can be converted to a number
 * 
 * @example
 * ```typescript
 * isNumberOrNumericString(123); // true
 * isNumberOrNumericString("123"); // true
 * isNumberOrNumericString("abc"); // false
 * ```
 */
export const isNumberOrNumericString = (
  value: unknown
): value is number | `${number}` => {
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed !== '' && !Number.isNaN(Number(trimmed));
  }
  return false;
};

/**
 * Safely convert value to number with fallback
 * Provides type-safe number conversion without throwing errors
 * 
 * @param value - Value to convert
 * @param fallback - Fallback value if conversion fails (default: 0)
 * @returns Converted number or fallback
 * 
 * @example
 * ```typescript
 * toNumber(123, 0); // 123
 * toNumber("456", 0); // 456
 * toNumber("abc", 0); // 0
 * toNumber(null, -1); // -1
 * ```
 */
export const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? fallback : value;
  }
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return fallback;
    
    const parsed = Number.parseFloat(trimmed);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  
  return fallback;
};

/**
 * Safely convert value to integer with fallback
 * 
 * @param value - Value to convert
 * @param fallback - Fallback value if conversion fails (default: 0)
 * @returns Converted integer or fallback
 * 
 * @example
 * ```typescript
 * toInteger("123.45", 0); // 123
 * toInteger("abc", -1); // -1
 * ```
 */
export const toInteger = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? fallback : Math.floor(value);
  }
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return fallback;
    
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  
  return fallback;
};

/**
 * Check if value is a non-empty string
 * 
 * @param value - Value to check
 * @returns True if value is a non-empty string
 * 
 * @example
 * ```typescript
 * isNonEmptyString("hello"); // true
 * isNonEmptyString(""); // false
 * isNonEmptyString(null); // false
 * ```
 */
export const isNonEmptyString = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim() !== '';
};

/**
 * Check if value is a valid array with items
 * 
 * @param value - Value to check
 * @returns True if value is a non-empty array
 * 
 * @example
 * ```typescript
 * isNonEmptyArray([1, 2, 3]); // true
 * isNonEmptyArray([]); // false
 * isNonEmptyArray(null); // false
 * ```
 */
export const isNonEmptyArray = <T>(value: unknown): value is T[] => {
  return Array.isArray(value) && value.length > 0;
};

/**
 * Check if value is a valid object (not null, not array)
 * 
 * @param value - Value to check
 * @returns True if value is a plain object
 * 
 * @example
 * ```typescript
 * isPlainObject({ key: 'value' }); // true
 * isPlainObject(null); // false
 * isPlainObject([]); // false
 * ```
 */
export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
};

/**
 * Assert that value is defined (not null or undefined)
 * Throws error if value is null or undefined
 * 
 * @param value - Value to check
 * @param errorMessage - Custom error message
 * @returns The value if it's defined
 * @throws Error if value is null or undefined
 * 
 * @example
 * ```typescript
 * const value = assertDefined(teamId, "Team ID is required");
 * ```
 */
export const assertDefined = <T>(
  value: T,
  errorMessage = "Value is required but was null or undefined"
): NonNullable<T> => {
  if (value === null || value === undefined) {
    throw new Error(errorMessage);
  }
  return value as NonNullable<T>;
};

/**
 * Check if value is null or undefined
 * 
 * @param value - Value to check
 * @returns True if value is null or undefined
 */
export const isNullOrUndefined = (value: unknown): value is null | undefined => {
  return value === null || value === undefined;
};

/**
 * Safely get string value with fallback
 * 
 * @param value - Value to convert
 * @param fallback - Fallback value (default: "")
 * @returns String value or fallback
 * 
 * @example
 * ```typescript
 * toString(123, ""); // "123"
 * toString(null, "N/A"); // "N/A"
 * ```
 */
export const toString = (value: unknown, fallback = ""): string => {
  if (isNullOrUndefined(value)) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // For objects/arrays, return fallback to avoid [object Object]
  return fallback;
};
